import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { BaseFiniteScrollCarousel } from "@faire/retailer-visitor-shared/components/Carousel/FiniteScrollCarousel/BaseFiniteScrollCarousel";
import { Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { isResponsiveProp } from "@faire/web--source/slate/Theme/ResponsiveProp";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { observer } from "mobx-react";
import React, { useRef, useState } from "react";
import styled, { css } from "styled-components";
var FiniteScrollCarouselBase = function(props) {
    var _scrollRef_current;
    var _useState = _sliced_to_array(useState(true), 2), isLeftEndReached = _useState[0], setIsLeftEndReached = _useState[1];
    var _useState1 = _sliced_to_array(useState(false), 2), isRightEndReached = _useState1[0], setIsRightEndReached = _useState1[1];
    var _useState2 = _sliced_to_array(useState(false), 2), isOverflow = _useState2[0], setIsOverflow = _useState2[1];
    var _useState3 = _sliced_to_array(useState("100vw"), 2), carouselWidth = _useState3[0], setCarouselWidth = _useState3[1];
    var scrollRef = useRef(null);
    var isMobile = useViewport().isMobile;
    var itemsPerSlide = props.itemsPerSlide, _props_peekGapPixel = props.peekGapPixel, peekGapPixel = _props_peekGapPixel === void 0 ? 0 : _props_peekGapPixel, _props_peekGapPercentage = props.peekGapPercentage, peekGapPercentage = _props_peekGapPercentage === void 0 ? 0 : _props_peekGapPercentage, itemGap = props.itemGap, renderItem = props.renderItem, items = props.items, className = props.className, latentPadding = props.latentPadding, onVisible = props.onVisible;
    React.useEffect(function() {
        if (scrollRef.current) {
            var width = scrollRef.current.getBoundingClientRect().width;
            setIsOverflow(scrollRef.current.scrollWidth > Math.round(width));
            setCarouselWidth("".concat(width, "px"));
        } else {
            setIsOverflow(false);
            setCarouselWidth("100vw");
        }
    }, [
        scrollRef,
        (_scrollRef_current = scrollRef.current) === null || _scrollRef_current === void 0 ? void 0 : _scrollRef_current.scrollWidth
    ]);
    var renderContentItem = function(item, index) {
        return /*#__PURE__*/ _jsx(ContentItem, {
            $itemGap: itemGap,
            $itemsPerSlide: realItemsPerSlide,
            $peekGapPixel: realPeekGapPixel,
            $peekGapPercentage: realPeekGapPercentage,
            $carouselWidth: carouselWidth,
            children: renderItem(item, index)
        }, index);
    };
    var onRightEndReached = function(inputRightEndReached) {
        setIsRightEndReached(inputRightEndReached);
    };
    var onLeftEndReached = function(inputLeftEndReached) {
        setIsLeftEndReached(inputLeftEndReached);
    };
    var shouldHidePreviousButton = !isOverflow || !!props.hideWhenEndReached && isLeftEndReached;
    var shouldHideNextButton = !isOverflow || !!props.hideWhenEndReached && isRightEndReached;
    var realItemsPerSlide = isResponsiveProp(itemsPerSlide) ? itemsPerSlide : {
        mobileAndAbove: itemsPerSlide
    };
    var realPeekGapPixel = isResponsiveProp(peekGapPixel) ? peekGapPixel : {
        mobileAndAbove: peekGapPixel
    };
    var realPeekGapPercentage = isResponsiveProp(peekGapPercentage) ? peekGapPercentage : {
        mobileAndAbove: peekGapPercentage
    };
    return /*#__PURE__*/ _jsx(StyledBaseFiniteScrollCarousel, {
        forwardRef: scrollRef,
        name: props.name,
        shouldHideLeftButton: shouldHidePreviousButton,
        shouldHideRightButton: shouldHideNextButton,
        isLeftMostReached: isLeftEndReached,
        isRightMostReached: isRightEndReached,
        onRightEndReached: onRightEndReached,
        onLeftEndReached: onLeftEndReached,
        onVisible: onVisible,
        peekGapPixel: useResponsiveValue(realPeekGapPixel),
        peekGapPercentage: useResponsiveValue(realPeekGapPercentage),
        className: className,
        enableDrag: true,
        children: /*#__PURE__*/ _jsxs(RowWrapper, {
            children: [
                items.map(renderContentItem),
                latentPadding ? /*#__PURE__*/ _jsx(Spacer, {
                    size: latentPadding
                }) : null,
                isMobile && !latentPadding ? /*#__PURE__*/ _jsx(Spacer, {
                    size: "2x"
                }) : null
            ]
        })
    });
};
var StyledBaseFiniteScrollCarousel = styled(BaseFiniteScrollCarousel.BaseFiniteScrollCarousel).withConfig({
    componentId: "sc-32bdb18f-0"
})([
    "",
    "{color:",
    ";}",
    "{color:",
    ";}"
], BaseFiniteScrollCarousel.Previous, function(param) {
    var isLeftMostReached = param.isLeftMostReached;
    return isLeftMostReached ? Core.text.subdued : Core.text.enabled;
}, BaseFiniteScrollCarousel.Next, function(param) {
    var isRightMostReached = param.isRightMostReached;
    return isRightMostReached ? Core.text.subdued : Core.text.enabled;
});
var ContentItem = styled.div.withConfig({
    componentId: "sc-32bdb18f-1"
})([
    "flex-shrink:0;padding:0 ",
    "px;width:",
    ";",
    "{width:",
    ";}",
    "{width:",
    ";}",
    "{width:",
    ";}"
], function(props) {
    return props.$itemGap / 2;
}, function(props) {
    var peekGapPixel = props.$peekGapPixel.mobileAndAbove;
    var peekGapPercentage = props.$peekGapPercentage.mobileAndAbove;
    var itemsPerSlide = props.$itemsPerSlide.mobileAndAbove;
    return css([
        "calc((100% - ",
        "px - ",
        "%) / ",
        ")"
    ], peekGapPixel, peekGapPercentage, itemsPerSlide);
}, responsiveMediaQueries.tabletAndAbove, function(props) {
    var _props_$peekGapPixel_tabletAndAbove;
    var peekGapPixel = (_props_$peekGapPixel_tabletAndAbove = props.$peekGapPixel.tabletAndAbove) !== null && _props_$peekGapPixel_tabletAndAbove !== void 0 ? _props_$peekGapPixel_tabletAndAbove : props.$peekGapPixel.mobileAndAbove;
    var _props_$peekGapPercentage_tabletAndAbove;
    var peekGapPercentage = (_props_$peekGapPercentage_tabletAndAbove = props.$peekGapPercentage.tabletAndAbove) !== null && _props_$peekGapPercentage_tabletAndAbove !== void 0 ? _props_$peekGapPercentage_tabletAndAbove : props.$peekGapPercentage.mobileAndAbove;
    var _props_$itemsPerSlide_tabletAndAbove;
    var itemsPerSlide = (_props_$itemsPerSlide_tabletAndAbove = props.$itemsPerSlide.tabletAndAbove) !== null && _props_$itemsPerSlide_tabletAndAbove !== void 0 ? _props_$itemsPerSlide_tabletAndAbove : props.$itemsPerSlide.mobileAndAbove;
    return css([
        "calc((100% - ",
        "px - ",
        "%) / ",
        ")"
    ], peekGapPixel, peekGapPercentage, itemsPerSlide);
}, responsiveMediaQueries.desktopAndAbove, function(props) {
    var _props_$peekGapPixel_desktopAndAbove, _ref;
    var peekGapPixel = (_ref = (_props_$peekGapPixel_desktopAndAbove = props.$peekGapPixel.desktopAndAbove) !== null && _props_$peekGapPixel_desktopAndAbove !== void 0 ? _props_$peekGapPixel_desktopAndAbove : props.$peekGapPixel.tabletAndAbove) !== null && _ref !== void 0 ? _ref : props.$peekGapPixel.mobileAndAbove;
    var _props_$peekGapPercentage_desktopAndAbove, _ref1;
    var peekGapPercentage = (_ref1 = (_props_$peekGapPercentage_desktopAndAbove = props.$peekGapPercentage.desktopAndAbove) !== null && _props_$peekGapPercentage_desktopAndAbove !== void 0 ? _props_$peekGapPercentage_desktopAndAbove : props.$peekGapPercentage.tabletAndAbove) !== null && _ref1 !== void 0 ? _ref1 : props.$peekGapPercentage.mobileAndAbove;
    var _props_$itemsPerSlide_desktopAndAbove, _ref2;
    var itemsPerSlide = (_ref2 = (_props_$itemsPerSlide_desktopAndAbove = props.$itemsPerSlide.desktopAndAbove) !== null && _props_$itemsPerSlide_desktopAndAbove !== void 0 ? _props_$itemsPerSlide_desktopAndAbove : props.$itemsPerSlide.tabletAndAbove) !== null && _ref2 !== void 0 ? _ref2 : props.$itemsPerSlide.mobileAndAbove;
    return css([
        "calc((100% - ",
        "px - ",
        "%) / ",
        ")"
    ], peekGapPixel, peekGapPercentage, itemsPerSlide);
}, responsiveMediaQueries.xLargeAndAbove, function(props) {
    var _props_$peekGapPixel_xLargeAndAbove, _ref, _ref1;
    var peekGapPixel = (_ref1 = (_ref = (_props_$peekGapPixel_xLargeAndAbove = props.$peekGapPixel.xLargeAndAbove) !== null && _props_$peekGapPixel_xLargeAndAbove !== void 0 ? _props_$peekGapPixel_xLargeAndAbove : props.$peekGapPixel.desktopAndAbove) !== null && _ref !== void 0 ? _ref : props.$peekGapPixel.tabletAndAbove) !== null && _ref1 !== void 0 ? _ref1 : props.$peekGapPixel.mobileAndAbove;
    var _props_$peekGapPercentage_xLargeAndAbove, _ref2, _ref3;
    var peekGapPercentage = (_ref3 = (_ref2 = (_props_$peekGapPercentage_xLargeAndAbove = props.$peekGapPercentage.xLargeAndAbove) !== null && _props_$peekGapPercentage_xLargeAndAbove !== void 0 ? _props_$peekGapPercentage_xLargeAndAbove : props.$peekGapPercentage.desktopAndAbove) !== null && _ref2 !== void 0 ? _ref2 : props.$peekGapPercentage.tabletAndAbove) !== null && _ref3 !== void 0 ? _ref3 : props.$peekGapPercentage.mobileAndAbove;
    var _props_$itemsPerSlide_xLargeAndAbove, _ref4, _ref5;
    var itemsPerSlide = (_ref5 = (_ref4 = (_props_$itemsPerSlide_xLargeAndAbove = props.$itemsPerSlide.xLargeAndAbove) !== null && _props_$itemsPerSlide_xLargeAndAbove !== void 0 ? _props_$itemsPerSlide_xLargeAndAbove : props.$itemsPerSlide.desktopAndAbove) !== null && _ref4 !== void 0 ? _ref4 : props.$itemsPerSlide.tabletAndAbove) !== null && _ref5 !== void 0 ? _ref5 : props.$itemsPerSlide.mobileAndAbove;
    return css([
        "calc((100% - ",
        "px - ",
        "%) / ",
        ")"
    ], peekGapPixel, peekGapPercentage, itemsPerSlide);
});
var RowWrapper = styled(Row).withConfig({
    componentId: "sc-32bdb18f-2"
})([
    "width:100%;"
]);
export var FiniteScrollCarousel;
(function(FiniteScrollCarousel_1) {
    FiniteScrollCarousel_1.SingleItemWrapper = ContentItem;
    FiniteScrollCarousel_1.FiniteScrollCarousel = observer(FiniteScrollCarouselBase);
})(FiniteScrollCarousel || (FiniteScrollCarousel = {}));
